import React, { useEffect } from 'react';
import { Mail, Phone, Linkedin } from 'lucide-react';
import { Box, Grid, Typography, IconButton, Divider, Link } from '@mui/material';
import { userSelector } from "../../../user-redux/selector";
import { useDispatch, useSelector } from "react-redux";
import { appSelector } from "../../../redux/selector";
import { getPrimaryResumeDetailsAction } from "../../../redux/action";

const UserDetails = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(userSelector);
  const { primaryResumeDetails } = useSelector(appSelector);
  const isCandidateUser = Number(user?.usertype) === 6;

  useEffect(() => {
    if (!isCandidateUser || !user?.candidate_id) return;
    dispatch(getPrimaryResumeDetailsAction({ candidate_id: user.candidate_id }));
  }, [dispatch, isCandidateUser, user?.candidate_id]);

  const getValidText = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value).trim();
      if (!text) continue;
      if (text.toLowerCase() === "undefined" || text.toLowerCase() === "null") continue;
      return text;
    }
    return "N/A";
  };

  const phoneNumber = isCandidateUser
    ? getValidText(user?.phone_number, primaryResumeDetails?.home_phone)
    : getValidText(user?.phone_number);

  const linkedInUrl = isCandidateUser
    ? getValidText(user?.linkedin_url, primaryResumeDetails?.linkedin_url)
    : getValidText(user?.linkedin_url);

  const hasValidLinkedInUrl = linkedInUrl !== "N/A";
  return (
    <Box sx={{ mt: 4 }}>
      <Grid container spacing={2}>
        {/* Email Address */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, p: 1, alignItems: 'center' }}>
            <IconButton
              size="small"
              sx={{
                bgcolor: '#e3f2fd',
                color: '#1e88e5',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Mail size={20} />
            </IconButton>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Email Address
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="text.primary">
                {getValidText(user?.username)}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ mt: 1 }} />
        </Grid>

        {/* Phone Number */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, p: 1, alignItems: 'center' }}>
            <IconButton
              size="small"
              sx={{
                bgcolor: '#e3f2fd',
                color: '#1e88e5',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Phone size={20} />
            </IconButton>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Phone Number
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="text.primary">
                {phoneNumber}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ mt: 1 }} />
        </Grid>

        {/* LinkedIn Profile */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, p: 1, alignItems: 'center' }}>
            <IconButton
              size="small"
              sx={{
                bgcolor: '#e3f2fd',
                color: '#1e88e5',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Linkedin size={20} />
            </IconButton>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                LinkedIn Profile
              </Typography>
              {hasValidLinkedInUrl ? (
                <Link
                  href={linkedInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  variant="body1"
                  fontWeight="medium"
                >
                  {linkedInUrl}
                </Link>
              ) : (
                <Typography variant="body1" fontWeight="medium" color="text.primary">
                  N/A
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserDetails;
